import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router'
import toast from 'react-hot-toast'
import Drawer from '../../components/common/Drawer'
import Label from '../../components/form/Label'
import Input from '../../components/form/input/InputField'
import Switch from '../../components/form/switch/Switch'
import { api, apiHeaders } from '../../configs/api'
import { useAuth } from '../../context/AuthContext'
import { useProfessional } from '../../context/ProfessionalContext'

type ProfessionalFormValues = { name: string; email: string; password: string }

const ProfessionalForm = ({
    type,
    open,
    handleClose
}: {
    type: 'create' | 'edit'
    open: boolean
    handleClose: () => void
}) => {
    const { unity, token, user, loadUsers } = useAuth()
    const { detail, getProfessional, loadProfessionals } = useProfessional()
    const [admin, setAdmin] = useState(false)
    const form = useForm<ProfessionalFormValues>({ mode: 'onChange' })
    const formRef = useRef<HTMLFormElement>(null)
    const navigate = useNavigate()

    const handleSave = async (data: ProfessionalFormValues) => {
        if (!unity || !token) return
        if (type === 'edit' && !detail?.professional.id) return toast.error('Nenhum profissional selecionado!')

        try {
            const response = await fetch(`${api}professional/${type === 'create' ? 'create' : 'update'}`, {
                method: 'POST',
                headers: apiHeaders(token),
                body: JSON.stringify({
                    ...data,
                    id: type === 'edit' ? detail?.professional.id : undefined,
                    image: type === 'edit' ? detail?.professional.image : null,
                    admin,
                    unityID: unity.unityID
                })
            })
            const result = await response.json()
            if (!response.ok || !result.success || !result.professional?.id) throw new Error(result.message)

            await loadProfessionals()
            await loadUsers()
            await getProfessional(result.professional.id)
            handleClose()
            toast.success(result.message || 'Profissional salvo com sucesso!')
            if (type === 'create') navigate(`/profissional/${result.professional.id}`)
        } catch (error) {
            toast.error(error instanceof Error && error.message ? error.message : 'Erro ao salvar profissional!')
        }
    }

    useEffect(() => {
        if (type === 'edit' && detail) {
            form.reset({ name: detail.professional.name, email: detail.professional.email, password: '' })
            setAdmin(detail.professional.admin)
        } else if (type === 'create') {
            form.reset({ name: '', email: '', password: '' })
            setAdmin(false)
        }
    }, [detail, form, open, type])

    return (
        <Drawer
            open={open}
            handleClose={handleClose}
            title={type === 'create' ? 'Adicionar profissional' : 'Editar profissional'}
            handleSave={() => formRef.current?.requestSubmit()}
        >
            <form ref={formRef} onSubmit={form.handleSubmit(handleSave)}>
                <div className='space-y-4'>
                    <div>
                        <Label required>Nome</Label>
                        <Input
                            {...form.register('name', { required: true })}
                            placeholder='Nome completo'
                            error={Boolean(form.formState.errors.name)}
                        />
                    </div>
                    <div>
                        <Label required>E-mail</Label>
                        <Input
                            {...form.register('email', { required: true })}
                            type='email'
                            placeholder='E-mail de acesso'
                            error={Boolean(form.formState.errors.email)}
                        />
                    </div>
                    <div>
                        <Label required={type === 'create'}>
                            {type === 'create' ? 'Senha' : 'Nova senha'}
                        </Label>
                        <Input
                            {...form.register('password', {
                                required: type === 'create',
                                validate: value => value === '' || value.length >= 8
                            })}
                            type='password'
                            placeholder={type === 'create' ? 'Mínimo de 8 caracteres' : 'Deixe em branco para manter'}
                            error={Boolean(form.formState.errors.password)}
                        />
                    </div>
                    <div>
                        <Label help='Administradores possuem acesso ampliado às unidades.'>Perfil</Label>
                        <Switch
                            label='Administrador'
                            checked={admin}
                            disabled={!user?.admin}
                            onChange={setAdmin}
                        />
                    </div>
                </div>
            </form>
        </Drawer>
    )
}

export default ProfessionalForm
